/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  isMergeCardsState,
  selectCurrentHorse,
  selectCurrentHorseIndex,
  selectCurrentState,
  selectDebugSpawnSource,
  selectEventLog,
  selectGameType,
  selectHorses,
  selectIsHistoryMode,
  selectIsOwnHorseIndexCurrent,
  selectLastRoundFinished,
  selectLeaveError,
  selectLeaveStatus,
  selectOwnHorses,
  selectPlayerId,
  selectRound,
  selectSelectedCardIndexLocal,
  selectTurnDicesPlayerId,
  selectTurnType,
  useSelector,
  useTranslation,
} from '~/app';
import { config, isGameplayHistoryReplayEnabled, tutorialConfig } from '~/app/config';
import { buildFullUrl } from '~/app/routes';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/Dialog';
import { Button } from '~/components/Form';
import { HistoryPanel } from '~/components/HistoryPanel';
import { leaveReset } from '~/game';
import { ChatVisibility, GameType } from '~/server/src/dtos';
import Turfmaster from '~/server/src/tmmodel';
import { useOnStateChange } from '~/utils';

import ActionUI from './action-ui/ActionUI';
import {
  sendLeaveGame,
  sendSelectCardDice,
  sendSelectMove,
  setHistoryCursor,
  setLastCard,
  setLastCardHorseIndex,
  setSelectedCardIndexLocal,
} from './actions';
import BarBottom from './bar-bottom/BarBottom';
import BarTop from './bar-top/BarTop';
import { FloatingMenuItemKey } from './bar-top/FloatingMenu';
import Chat from './chat/Chat';
import MergeView from './merge/MergeView';
import Report from './report/Report';
import Statistics, { StatisticsModalState } from './statistics/Statistics';

/** Notifies parent when 3s shuffle period ends; parent derives shuffling from session id (no sync setState in effect). */
function DiceShuffleReporter({
  sessionId,
  onShuffleEnded,
}: {
  sessionId: number;
  onShuffleEnded: (id: number) => void;
}): null {
  useEffect(() => {
    const id = window.setTimeout(() => onShuffleEnded(sessionId), 3000);
    return () => window.clearTimeout(id);
  }, [sessionId, onShuffleEnded]);
  return null;
}

interface OverlayProps {
  onCloseHandler: () => void;
  /** If true, we're in replay mode - leave game should just call onCloseHandler instead of sending server message */
  isReplayMode?: boolean;
  /** If true, we're in spectator mode - no game actions, leave calls onCloseHandler */
  isSpectatorMode?: boolean;
  /** Game chat visibility: when 'playersOnly', spectators cannot send messages. Default 'public'. */
  chatVisibility?: ChatVisibility;
}

const Overlay = ({
  onCloseHandler,
  isReplayMode = false,
  isSpectatorMode = false,
  chatVisibility = 'public',
}: OverlayProps): JSX.Element => {
  const analytics = useFirebaseAnalytics();
  const { locale } = useTranslation();
  // Local state for panel visibility (UI only)
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [historyStartIndex, setHistoryStartIndex] = useState<number | undefined>(undefined);
  const eventLog = useSelector(selectEventLog);
  // Redux state for actual history mode (viewing past events) - used for blocking interactions
  const isHistoryMode = useSelector(selectIsHistoryMode);

  // Show history button whenever we're on the game screen (e.g. host waiting for players or game running).
  // Panel shows empty timeline when no events yet. Disabled in production when gameplay history/replay is off.
  const isHistoryAvailable = isGameplayHistoryReplayEnabled;

  const currentState = useSelector(selectCurrentState);
  const playerId = useSelector(selectPlayerId);
  const turnType = useSelector(selectTurnType);
  const turnDicesPlayerId = useSelector(selectTurnDicesPlayerId);
  const gameType = useSelector(selectGameType);
  const debugSpawnSource = useSelector(selectDebugSpawnSource);
  const round = useSelector(selectRound);
  const isLastRoundFinished = useSelector(selectLastRoundFinished);
  const horses = useSelector(selectHorses);
  const ownHorses = useSelector(selectOwnHorses);
  const currentHorse = useSelector(selectCurrentHorse);
  const currentHorseIndex = useSelector(selectCurrentHorseIndex);

  const selectedCardIndexLocal = useSelector(selectSelectedCardIndexLocal);

  const leaveStatus = useSelector(selectLeaveStatus);
  const leaveError = useSelector(selectLeaveError);

  const [statisticsModalState, setStatisticsModalState] = useState<StatisticsModalState>('closed');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTriggerElement, setReportTriggerElement] = useState<Element | null>(null);
  const [leaveGameDialogOpen, setLeaveGameDialogOpen] = useState(false);
  const [selectedOwnHorseIndex, setSelectedOwnHorseIndex] = useState(0);
  const [prevSyncedCurrentHorseIndex, setPrevSyncedCurrentHorseIndex] = useState<number | null>(
    null,
  );
  /** True after user submits a move; cleared when leaving SELECTDICES or round changes. Used to disable bonus card until dice phase ends. */
  const [moveJustSubmitted, setMoveJustSubmitted] = useState(false);
  const [prevRoundForMoveSubmitted, setPrevRoundForMoveSubmitted] = useState(round);
  /** True after user submits bonus cards; cleared when state no longer allows ADDBONUSCARDS or round changes. */
  const [bonusJustSubmitted, setBonusJustSubmitted] = useState(false);
  const [prevRoundForBonusSubmitted, setPrevRoundForBonusSubmitted] = useState(round);

  const isOwnHorseIndexCurrent = useSelector((state) =>
    selectIsOwnHorseIndexCurrent(state, selectedOwnHorseIndex),
  );

  const dispatch = useDispatch();

  /**
   * Helper function to wrap sendSelectCardDice event to fill in all necessary parameters
   */
  const setSelectedCardDice = useCallback(
    (index) => {
      dispatch(
        sendSelectCardDice({ playerid: playerId, horseid: currentHorseIndex, carddiceid: index }),
      );
    },
    [currentHorseIndex, dispatch, playerId],
  );

  // Always show user's selected horse for which card deck is displayed; sync selection when turn changes to our horse (auto-switch).
  // Adjust state during render (React docs: "Adjusting some state when a prop changes"); use state (not ref) so we don't read ref during render.
  const displayedOwnHorseIndex = selectedOwnHorseIndex;
  if (currentHorse?.playerId === playerId) {
    const idx = ownHorses.indexOf(currentHorse);
    if (idx >= 0 && currentHorseIndex !== prevSyncedCurrentHorseIndex) {
      setPrevSyncedCurrentHorseIndex(currentHorseIndex);
      setSelectedOwnHorseIndex(idx);
    }
  } else if (prevSyncedCurrentHorseIndex !== null) {
    setPrevSyncedCurrentHorseIndex(null);
  }

  // canAddBonusCardsNow: server only accepts ADDBONUSCARDS when currentState is in stateMatrix and array includes ADDBONUSCARDS (used to clear bonusJustSubmitted)
  const canAddBonusCardsNow =
    (Turfmaster.stateMatrix as Record<number, number[] | undefined>)[currentState]?.includes(
      Turfmaster.stateMachine.ADDBONUSCARDS,
    ) ?? false;

  // Clear "move just submitted" when we leave dice selection or round changes (adjust state during render; only set when value would change to avoid re-render loop)
  if (currentState !== Turfmaster.stateMachine.SELECTDICES && moveJustSubmitted) {
    setMoveJustSubmitted(false);
  }
  if (round !== prevRoundForMoveSubmitted) {
    if (moveJustSubmitted) setMoveJustSubmitted(false);
    setPrevRoundForMoveSubmitted(round);
  }
  // Clear "bonus just submitted" when state no longer allows ADDBONUSCARDS or round changes
  if (!canAddBonusCardsNow && bonusJustSubmitted) {
    setBonusJustSubmitted(false);
  }
  if (round !== prevRoundForBonusSubmitted) {
    if (bonusJustSubmitted) setBonusJustSubmitted(false);
    setPrevRoundForBonusSubmitted(round);
  }

  /**
   * If it is our turn to select a card and the right card deck is shown then
   * tell the controller when a card selection takes place.
   * Uses displayedOwnHorseIndex so the horse we validate against is the one whose cards are shown.
   * In SELECTMOVE, index === -1 unselects the card; index >= 0 switches to another card.
   * @param index index of the selected card, or -1 to unselect
   */
  const onCardSelected = (index: number) => {
    if (isSpectatorMode || isHistoryMode) return;
    if (horses && currentHorse && !isMergeCardsState(currentState)) {
      const isSelectMove = currentState === Turfmaster.stateMachine.SELECTMOVE;
      const isSelectCardDice = currentState === Turfmaster.stateMachine.SELECTCARDDICE;
      const selectedHorse = horses.filter((horse) => horse.playerId === playerId)[
        displayedOwnHorseIndex
      ];
      const isOurTurnWithCorrectHorse =
        turnType === Turfmaster.turntypes.CARDS &&
        currentHorse.playerId === playerId &&
        currentHorse === selectedHorse;

      if (isOurTurnWithCorrectHorse && isSelectMove && index === -1) {
        dispatch(setSelectedCardIndexLocal(-1));
        setSelectedCardDice(-1);
        return;
      }

      if (isOurTurnWithCorrectHorse && (isSelectCardDice || isSelectMove) && index >= 0) {
        if (selectedCardIndexLocal !== index) {
          dispatch(setSelectedCardIndexLocal(index));
          setSelectedCardDice(index);
        }
      }
    }
  };

  /**
   * Selection of own horse in left bar to switch between card decks
   */
  const onOwnHorseSelected = useCallback(
    (ownHorse) => {
      if (isSpectatorMode || isHistoryMode) return;
      setSelectedOwnHorseIndex(ownHorse);
      analytics?.logEvent('view_horse_cards', {
        round,
        turnType,
        gameType,
        isCurrent: isOwnHorseIndexCurrent,
      });
    },
    [analytics, gameType, isHistoryMode, isOwnHorseIndexCurrent, isSpectatorMode, round, turnType],
  );

  const onCardMoveSelected = (index: number) => {
    if (isSpectatorMode || isHistoryMode) return;
    dispatch(
      sendSelectMove({
        playerid: playerId,
        horseid: currentHorseIndex,
        carddiceid: selectedCardIndexLocal,
        moveid: index,
      }),
    );
    dispatch(setSelectedCardIndexLocal(-1));
    setMoveJustSubmitted(true);
  };

  // Dice-phase: SELECTDICES (choose dice after roll) or SELECTCARDDICE (choose dice for handicap)
  const isDicePhase =
    turnType === Turfmaster.turntypes.DICES &&
    (currentState === Turfmaster.stateMachine.SELECTDICES ||
      currentState === Turfmaster.stateMachine.SELECTCARDDICE);

  // During dice phase: show controls for the acting player regardless of viewed horse. Card phase: show when current horse is ours (regardless of which horse tab is viewed).
  const shouldShowTurnControls = isDicePhase
    ? currentState === Turfmaster.stateMachine.SELECTDICES
      ? playerId === turnDicesPlayerId
      : currentHorse != null && currentHorse.playerId === playerId
    : currentHorse != null && currentHorse.playerId === playerId;

  // Horse tab only changes which deck is visible; switching allowed at any time.
  const canSwitchViewedHorse = true;

  /** Active dice pick phase (shuffle + pick). SELECTDICESOPP result is shown in BarTop only (no duplicate dice in TurnControls). */
  const isDiceSelectionPhase =
    turnType === Turfmaster.turntypes.DICES && currentState === Turfmaster.stateMachine.SELECTDICES;

  /** Dice pick window incl. server-confirmed dice result state (used for last-card linger lifecycle). */
  const isDicePickWindow =
    turnType === Turfmaster.turntypes.DICES &&
    (currentState === Turfmaster.stateMachine.SELECTDICES ||
      currentState === Turfmaster.stateMachine.SELECTDICESOPP);

  /** Pass to BarBottom: SELECTDICES only; BarTop shows dice + pick after SELECTDICESOPP */
  const isDiceSelection = isDiceSelectionPhase;

  // Dice shuffle: session id so we know when 3s has ended; child reports after 3s (async only).
  // Initialize to 1 when already in SELECTDICES (e.g. after reload) so first render is "shuffling".
  const [shuffleSessionId, setShuffleSessionId] = useState(() => (isDiceSelectionPhase ? 1 : 0));
  const [lastEndedShuffleSessionId, setLastEndedShuffleSessionId] = useState(0);
  const prevIsDiceSelectionPhaseRef = useRef(isDiceSelectionPhase);
  useEffect(() => {
    if (isDiceSelectionPhase && !prevIsDiceSelectionPhaseRef.current) {
      setShuffleSessionId((s) => s + 1);
    }
    prevIsDiceSelectionPhaseRef.current = isDiceSelectionPhase;
  }, [isDiceSelectionPhase]);
  const isDiceSelectionReady =
    isDiceSelectionPhase && shuffleSessionId > 0 && lastEndedShuffleSessionId >= shuffleSessionId;
  const isDiceShuffling = isDiceSelectionPhase && !isDiceSelectionReady;
  const handleShuffleEnded = useCallback((sessionId: number) => {
    setLastEndedShuffleSessionId((prev) => (sessionId > prev ? sessionId : prev));
  }, []);

  // Last-card linger: keep last played card visible a bit beyond dice shuffle.
  // Hide/clear it when server confirms dice selection (SELECTDICESOPP),
  // or after a short fallback delay once dice become selectable (prevents infinite linger).
  const LAST_CARD_DICE_LINGER_FALLBACK_MS = 2000;
  const [lastCardLingerExpiredSessionId, setLastCardLingerExpiredSessionId] = useState<
    number | null
  >(null);
  const lingerTimerRef = useRef<number | null>(null);
  const didClearLastCardAfterLingerRef = useRef(false);
  const prevShuffleSessionIdRef = useRef(shuffleSessionId);
  const isLastCardDiceLingerExpired =
    isDicePickWindow &&
    (currentState === Turfmaster.stateMachine.SELECTDICESOPP ||
      lastCardLingerExpiredSessionId === shuffleSessionId);

  useEffect(() => {
    // Reset when leaving dice pick window (SELECTDICES / SELECTDICESOPP).
    if (!isDicePickWindow) {
      didClearLastCardAfterLingerRef.current = false;
      if (lingerTimerRef.current != null) window.clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = null;
      prevShuffleSessionIdRef.current = shuffleSessionId;
      return;
    }

    // New shuffle session: reset linger state.
    if (shuffleSessionId !== prevShuffleSessionIdRef.current) {
      prevShuffleSessionIdRef.current = shuffleSessionId;
      didClearLastCardAfterLingerRef.current = false;
      if (lingerTimerRef.current != null) window.clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = null;
    }
  }, [isDicePickWindow, shuffleSessionId]);

  useEffect(() => {
    if (turnType !== Turfmaster.turntypes.DICES) return;

    // After shuffle: start fallback timer only while still in SELECTDICES.
    if (!isDiceSelectionPhase) return;
    if (currentState !== Turfmaster.stateMachine.SELECTDICES) return;
    if (!isDiceSelectionReady) return;
    if (isLastCardDiceLingerExpired) return;
    if (lingerTimerRef.current != null) return;

    lingerTimerRef.current = window.setTimeout(() => {
      lingerTimerRef.current = null;
      setLastCardLingerExpiredSessionId(shuffleSessionId);
    }, LAST_CARD_DICE_LINGER_FALLBACK_MS);
  }, [
    currentState,
    shuffleSessionId,
    isDiceSelectionPhase,
    isDiceSelectionReady,
    isLastCardDiceLingerExpired,
    turnType,
  ]);

  useEffect(() => {
    if (turnType !== Turfmaster.turntypes.DICES) return;

    const shouldClear =
      currentState === Turfmaster.stateMachine.SELECTDICESOPP ||
      (currentState === Turfmaster.stateMachine.SELECTDICES && isLastCardDiceLingerExpired);

    if (!shouldClear) return;
    if (didClearLastCardAfterLingerRef.current) return;
    didClearLastCardAfterLingerRef.current = true;
    dispatch(setLastCardHorseIndex(-1));
    dispatch(setLastCard(-1));
  }, [currentState, dispatch, isLastCardDiceLingerExpired, turnType]);

  useOnStateChange(
    currentState,
    useCallback((newCurrentState) => {
      if (newCurrentState === Turfmaster.stateMachine.ENDROUND) {
        setStatisticsModalState('timed');
      }
    }, []),
  );

  const onFloatingMenuItemClicked = useCallback(
    (key: FloatingMenuItemKey, event?: Event) => {
      switch (key) {
        case 'table':
          window.open(buildFullUrl('/table'), '_blank');
          analytics?.logEvent('open_table');
          break;
        case 'worldRankings':
          window.open(buildFullUrl('/toplist'), '_blank');
          analytics?.logEvent('open_worldRankings');
          break;
        case 'statistics':
          setStatisticsModalState('open');
          analytics?.logEvent('show_statistics');
          break;
        case 'history':
          setIsHistoryPanelOpen((prev) => {
            const newValue = !prev;
            if (newValue) {
              // Quick Rewind: enter history mode one step back
              const oneStepBack = Math.max(0, eventLog.length - 1);
              setHistoryStartIndex(oneStepBack);
            } else {
              setHistoryStartIndex(undefined);
            }
            return newValue;
          });
          break;

        case 'report':
          setReportTriggerElement(
            event?.currentTarget instanceof Element ? event.currentTarget : null,
          );
          setReportModalOpen(true);
          break;
        case 'instructions': {
          const pdfUrl =
            tutorialConfig.instructionsPdf[locale as keyof typeof tutorialConfig.instructionsPdf] ||
            tutorialConfig.instructionsPdf.en;
          window.open(pdfUrl, '_blank');
          analytics?.logEvent('open_instructions', {
            event_category: 'tutorial',
            event_label: 'instructions_pdf',
            language: locale,
          });
          break;
        }
        case 'youtube':
          window.open(tutorialConfig.youtubeChannel, '_blank');
          analytics?.logEvent('open_youtube', {
            event_category: 'tutorial',
            event_label: 'youtube_channel',
          });
          break;
        case 'discord':
          if (config.discordEnabled && config.discordInviteUrl) {
            window.open(config.discordInviteUrl, '_blank');
            analytics?.logEvent('open_discord', {
              event_category: 'community',
              event_label: 'discord_community',
            });
          }
          break;
        case 'whatsapp':
          if (config.whatsappEnabled && config.whatsappInviteUrl) {
            window.open(config.whatsappInviteUrl, '_blank');
            analytics?.logEvent('open_whatsapp', {
              event_category: 'community',
              event_label: 'whatsapp_community',
            });
          }
          break;
        case 'developer': {
          const homepageUrl =
            tutorialConfig.creatorHomepage[locale as keyof typeof tutorialConfig.creatorHomepage] ||
            tutorialConfig.creatorHomepage.en;
          window.open(homepageUrl, '_blank');
          analytics?.logEvent('open_developer', {
            event_category: 'tutorial',
            event_label: 'developer_homepage',
            language: locale,
          });
          break;
        }
        case 'leaveGame':
          if (isReplayMode || isSpectatorMode) {
            // In replay or spectator mode, just close/navigate back instead of sending server message
            onCloseHandler();
          } else {
            setLeaveGameDialogOpen(true);
          }
          break;
      }
    },
    [analytics, eventLog.length, isReplayMode, isSpectatorMode, locale, onCloseHandler],
  );

  const handleHistoryClose = useCallback(() => {
    setIsHistoryPanelOpen(false);
    // Don't reset historyStartIndex - keep history mode active when panel closes
  }, []);

  const handleExitHistoryMode = useCallback(() => {
    dispatch(setHistoryCursor({ kind: 'live' }));
    setHistoryStartIndex(undefined);
  }, [dispatch]);

  const handleHistoryToggle = useCallback(() => {
    setIsHistoryPanelOpen((prev) => {
      const newValue = !prev;
      if (newValue) {
        // Quick Rewind: enter history mode one step back
        const oneStepBack = Math.max(0, eventLog.length - 1);
        setHistoryStartIndex(oneStepBack);
      } else {
        setHistoryStartIndex(undefined);
      }
      // Don't dispatch setHistoryMode here - let HistoryPanel manage it based on eventIndex
      return newValue;
    });
  }, [eventLog.length]);

  const handleLeaveGameConfirm = useCallback(() => {
    dispatch(sendLeaveGame({ playerid: playerId }));
    // Navigation will happen after server confirms the leave
  }, [dispatch, playerId]);

  const { t } = useTranslation();
  const isLeavePending = leaveStatus === 'pending';
  const isLeaveFailed = leaveStatus === 'failed';
  const leaveErrorUiText = useMemo(() => {
    if (!isLeaveFailed) return null;
    const code = typeof leaveError?.message === 'string' ? leaveError.message.trim() : '';
    switch (code) {
      case 'leave_ack_timeout':
        return t('game_leaveGameFailedReason_timeout');
      case 'leave_not_allowed':
        return t('game_leaveGameFailedReason_notAllowed');
      case 'leave_failed':
        return t('game_leaveGameFailedReason_server');
      default:
        return null;
    }
  }, [isLeaveFailed, leaveError?.message, t]);

  useEffect(() => {
    if (leaveGameDialogOpen) {
      dispatch(leaveReset());
    }
  }, [dispatch, leaveGameDialogOpen]);

  return (
    <div className="pointer-events-none fixed top-0 right-0 bottom-0 left-0 h-screen w-screen">
      {isDiceSelectionPhase && !isDiceSelectionReady && (
        <DiceShuffleReporter sessionId={shuffleSessionId} onShuffleEnded={handleShuffleEnded} />
      )}
      <ActionUI />

      <BarBottom
        selectedCardLocalIndex={selectedCardIndexLocal}
        selectedOwnHorseIndex={displayedOwnHorseIndex}
        onCardSelected={onCardSelected}
        onCardMoveSelected={onCardMoveSelected}
        isDiceShuffling={isDiceShuffling}
        isDiceSelection={isDiceSelection}
        shouldShowTurnControls={shouldShowTurnControls}
        canSwitchViewedHorse={canSwitchViewedHorse}
        selectedOwnHorse={displayedOwnHorseIndex}
        onOwnHorseSelected={onOwnHorseSelected}
        isSpectatorMode={isSpectatorMode}
        isHistoryMode={isHistoryMode}
        isHistoryPanelOpen={isHistoryPanelOpen}
        moveJustSubmitted={moveJustSubmitted}
        bonusJustSubmitted={bonusJustSubmitted}
        onBonusCardsSubmitted={() => setBonusJustSubmitted(true)}
      />

      {gameType && gameType !== GameType.Training && (
        <Chat
          initVisible={debugSpawnSource !== 'common'}
          isSpectatorMode={isSpectatorMode}
          isHistoryMode={isHistoryMode}
          spectatorsCanSend={!isSpectatorMode || chatVisibility === 'public'}
        />
      )}

      <BarTop
        isDiceShuffling={isDiceShuffling}
        isDiceSelectionReady={isDiceSelectionReady}
        isLastCardDiceLingerExpired={isLastCardDiceLingerExpired}
        onFloatingMenuItemClicked={onFloatingMenuItemClicked}
        isSpectatorMode={isSpectatorMode}
        isHistoryMode={isHistoryMode}
      />

      <Statistics
        state={statisticsModalState}
        isFinalRoundTransition={isLastRoundFinished}
        onClose={() => setStatisticsModalState('closed')}
      />
      <Report
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setReportTriggerElement(null);
        }}
        triggerElement={reportTriggerElement}
      />
      <MergeView isSpectatorMode={isSpectatorMode} isHistoryMode={isHistoryMode} />

      {/* Amber viewport border when history mode is active */}
      {isHistoryMode && (
        <>
          {/* Outer border using box-shadow for reliable visibility */}
          <div
            className="pointer-events-none fixed inset-0 z-99 rounded-lg"
            style={{
              boxShadow: 'inset 0 0 0 4px rgba(245, 158, 11, 0.6)',
            }}
          />
          {/* Subtle amber background tint */}
          <div className="pointer-events-none fixed inset-0 z-98 rounded-lg bg-amber-500/5" />
        </>
      )}

      {/* History panel and button - shown whenever on game screen (including waiting for players) */}
      {isHistoryAvailable && (
        <HistoryPanel
          onClose={handleHistoryClose}
          initialEventIndex={historyStartIndex}
          isHistoryMode={isHistoryPanelOpen}
          isSpectatorMode={isSpectatorMode}
          onToggle={handleHistoryToggle}
          onExitHistoryMode={handleExitHistoryMode}
        />
      )}

      <Dialog open={leaveGameDialogOpen} onOpenChange={setLeaveGameDialogOpen}>
        <DialogContent size="sm" showCloseButton={true}>
          <DialogTitle>{t('button_leaveGame')}</DialogTitle>
          <DialogDescription>
            {isLeavePending
              ? t('game_leaveGamePending')
              : gameType === GameType.Training
                ? t('game_leaveGameConfirm_training')
                : playerId === 0
                  ? t('game_leaveGameConfirm_multiplayer_host')
                  : t('game_leaveGameConfirm_multiplayer')}
          </DialogDescription>
          {isLeaveFailed && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <div className="font-medium">{t('game_leaveGameFailedTitle')}</div>
              <div className="mt-1">{t('game_leaveGameFailedText')}</div>
              {leaveErrorUiText && (
                <div className="mt-1 text-xs text-red-700/80">{leaveErrorUiText}</div>
              )}
            </div>
          )}
          <div className="flex flex-col justify-end gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              onClick={() => setLeaveGameDialogOpen(false)}
              disabled={isLeavePending}
              className="flex items-center justify-center space-x-2 border border-gray-300 bg-white px-3 py-2 whitespace-nowrap text-gray-700"
              size="sm"
            >
              <svg
                className="h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span className="text-sm">{t('cancel')}</span>
            </Button>
            {isLeaveFailed && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    // Exit immediately even if server leave failed; ensures user is never trapped on board.
                    setLeaveGameDialogOpen(false);
                    dispatch(leaveReset());
                    onCloseHandler();
                  }}
                  className="flex items-center justify-center space-x-2 border border-gray-300 bg-white px-3 py-2 whitespace-nowrap text-gray-700"
                  size="sm"
                >
                  <span className="text-sm">{t('game_leaveGameExitAnyway')}</span>
                </Button>
                <Button
                  variant="danger"
                  onClick={handleLeaveGameConfirm}
                  disabled={isLeavePending}
                  className="flex items-center justify-center space-x-2 bg-red-600 px-3 py-2 whitespace-nowrap text-white"
                  size="sm"
                >
                  <span className="text-sm">{t('game_leaveGameRetry')}</span>
                </Button>
              </>
            )}
            <Button
              variant="danger"
              onClick={handleLeaveGameConfirm}
              disabled={isLeavePending || isLeaveFailed}
              className="flex items-center justify-center space-x-2 bg-red-600 px-3 py-2 whitespace-nowrap text-white"
              size="sm"
            >
              <svg
                className="h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span className="text-sm">{t('button_leaveGame')}</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Overlay;
